import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import Measure from 'react-measure';

import Box from '@glass/web/components/base/Box';
import makeStyles from '@glass/web/modules/theme/makeStyles';

const useStyles = makeStyles()({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  content: {
    transformOrigin: 'top left',
    position: 'relative',
  },
});

function ScaleWidth({ children, width: scaleWidth, maxScale }) {
  const { classes } = useStyles();
  const [width, setWidth] = useState(0);
  const scale = useMemo(
    () => Math.max(Math.min(width / scaleWidth, maxScale), 0),
    [width, scaleWidth, maxScale],
  );
  const handleResize = useCallback(
    (contentRect) => {
      if (contentRect?.bounds?.width !== width) {
        setWidth(contentRect.bounds.width);
      }
    },
    [width],
  );

  const rootStyle = useMemo(
    () => ({
      paddingBottom: scale > 1 ? `${(scale - 1) * 100}%` : undefined,
    }),
    [scale],
  );
  const contentStyle = useMemo(
    () => ({
      width: scaleWidth,
      transform: `scale(${scale})`,
      paddingBottom: rootStyle.paddingBottom,
    }),
    [scale, scaleWidth, rootStyle],
  );

  return (
    <Measure bounds onResize={handleResize}>
      {({ measureRef }) => {
        return (
          <Box ref={measureRef} className={classes.root} style={rootStyle}>
            <div className={classes.content} style={contentStyle}>
              {typeof children === 'function' ? children({ scale }) : children}
            </div>
          </Box>
        );
      }}
    </Measure>
  );
}

ScaleWidth.defaultProps = {
  maxScale: 3,
};

ScaleWidth.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.object]),
  maxScale: PropTypes.number,
  width: PropTypes.number.isRequired,
};

export default ScaleWidth;
